/**
 * Semantic → Mantine adapter.
 *
 * The single, pure boundary where the framework-agnostic `@teo/design-tokens`
 * package is translated into the shapes Mantine's `createTheme` expects. It has
 * no React/Mantine-runtime side effects — every export is a value derived
 * deterministically from the tokens, so it is trivially unit-testable and the
 * `theme.ts` it feeds stays declarative (no hand-maintained hex).
 *
 * Three framework-coupling decisions live HERE, not in the token package
 * (whose names also ship into W3C/Style-Dictionary/Tailwind exports):
 *
 *  1. `black = ink`. Mantine reserves a `theme.black` slot; TEO's brand
 *     foreground is `commonColors.ink` (#470047 = purple[7]). The mapping is an
 *     adapter concern — pushing a `black` name back into the package would
 *     wrongly imply #000000 in the agnostic exports.
 *  2. Ramps are already Mantine-shaped. Each `ColorRamp` is a frozen 10-step
 *     tuple in 0→9 order, so it maps onto a `MantineColorsTuple` with NO value
 *     translation — `rampToTuple` is an identity-with-length-assertion.
 *  3. Elevation is intentionally NOT wired into `theme.shadows`. The token
 *     `elevation` scale stays defined-but-unused so Mantine's default shadows
 *     (and therefore every `<Paper>`) render exactly as before. See theme.ts.
 *
 * Light-mode only: the TEO product has no dark mode, so the adapter produces a
 * single light-mode theme — there is no color-scheme branching anywhere here.
 */
import type { MantineColorsTuple } from '@mantine/core';
import {
  type ColorRamp,
  commonColors,
  fontFamily,
  fontSize,
  fontWeight,
  headingSize,
  lineHeight,
  primitiveRamps,
  radius,
  spacing,
} from '@teo/design-tokens';

/** The Mantine t-shirt scale keys, in order. Selecting these explicitly drops any
 *  extra token key Mantine has no slot for (e.g. `radius.full` = 9999px), so the
 *  generated scale vars stay byte-identical to Mantine's defaults. */
const TSHIRT_KEYS = ['xs', 'sm', 'md', 'lg', 'xl'] as const;
type TshirtKey = (typeof TSHIRT_KEYS)[number];

function tshirtScale(scale: Record<TshirtKey, string>): Record<TshirtKey, string> {
  const out = {} as Record<TshirtKey, string>;
  for (const key of TSHIRT_KEYS) {
    out[key] = scale[key];
  }
  return out;
}

/** Passthrough (decision #2): the length check turns an impossible-by-construction
 *  shape mismatch into a loud failure rather than a silently malformed theme. */
export function rampToTuple(ramp: ColorRamp): MantineColorsTuple {
  if (ramp.length !== 10) {
    throw new Error(`Expected a 10-step ramp, got ${ramp.length}`);
  }
  return ramp;
}

/** Build Mantine's `theme.colors` from the primitive ramps. purple/orange/green
 *  are byte-identical to the legacy `theme.ts` arrays; red is the new real
 *  gradation (was flat `#b63333` ×10); neutral equals Mantine's default gray.
 *
 *  `blue` was withheld for a while because publishing it overrides Mantine's
 *  built-in blue at every raw `color="blue"` call site, and that re-skin needed
 *  to be a deliberate decision rather than a side effect of adding the ramp.
 *  That migration has since happened for the status and category surfaces; the
 *  `<Alert>` and `<ActionIcon>` inventories are left for a later pass so those
 *  files are touched once rather than twice, and they re-skin to the brand blue
 *  in the meantime. */
export function tokensToMantineColors(): Record<string, MantineColorsTuple> {
  const out: Record<string, MantineColorsTuple> = {};
  for (const [name, ramp] of Object.entries(primitiveRamps)) {
    out[name] = rampToTuple(ramp);
  }
  return out;
}

/** Mantine's reserved `theme.black` slot — sourced from brand `ink`, not a
 *  literal black (decision #1). */
export const THEME_BLACK = commonColors.ink;

/** Mantine's `theme.white` slot, sourced from the token white (#ffffff). */
export const THEME_WHITE = commonColors.white;

/** `theme.radius` from the token radius scale (xs…xl; `full` dropped). */
export function tokensToRadius(): Record<TshirtKey, string> {
  return tshirtScale(radius);
}

/** `theme.spacing` from the token spacing scale. */
export function tokensToSpacing(): Record<TshirtKey, string> {
  return tshirtScale(spacing);
}

/** `theme.fontSizes` from the token font-size scale. */
export function tokensToFontSizes(): Record<TshirtKey, string> {
  return tshirtScale(fontSize);
}

/** `theme.lineHeights` from the token line-height scale. */
export function tokensToLineHeights(): Record<TshirtKey, string> {
  return tshirtScale(lineHeight);
}

/** Brand body font stack ("Space Grotesk", …). */
export const THEME_FONT_FAMILY = fontFamily.body;

/** Brand heading font stack (same family as body, per the brand theme). */
export const THEME_HEADING_FONT_FAMILY = fontFamily.heading;

/** Heading weight — the semibold (600) token, as Mantine expects a string. */
export const THEME_HEADING_FONT_WEIGHT = fontWeight.semibold;

/**
 * `theme.headings.sizes`, straight from the fluid heading-size tokens.
 *
 * A constant rather than a function, because there is nothing to convert: the
 * token is already `{ fontSize, lineHeight }` per level, which is the shape
 * Mantine wants. It sat behind a `tokensToHeadingSizes()` for as long as that
 * function had min/max fields to strip, and kept the shape of a converter after
 * the fields went. Mantine writes each `fontSize`
 * verbatim into `--mantine-hN-font-size`, so a `clamp()` string arrives at every
 * `<Title>` and every `size="hN"` override with no media query and no
 * per-component work.
 */
export const THEME_HEADING_SIZES = headingSize;
